/**
 * Client-side renderer for the server-authoritative NPC crew.
 *
 * When the optional CC0 crew model is available (and the profile is not the
 * reduced mobile LOD) each crew member renders a skinned GLB character driven
 * by a role/task animation:
 * - `idle` -> Idle
 * - `trimming` -> Wave (arm motion while working the sail lines)
 * - `loading` -> Sword (shoving a cannonball home)
 * - `repairing` -> Punch (hammer swing)
 *
 * Otherwise — model missing, load still in flight, or the mobile LOD profile —
 * the class keeps the original procedural capsule avatar (merged body+head per
 * role, with a hammer for the repair hand). The public API (`group`, `sync`,
 * `update`, `dispose`, `whenReady`, `isModelReady`) is unchanged for existing
 * callers, which continue to work; `whenReady()` is the capture-path hook.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createSeededRandom } from '../utils/random';
import { ModelLibrary, type ModelHandle } from './ModelLibrary';

/** Server crew task the client animates against (decoupled from the schema). */
export type CrewAvatarTask = 'idle' | 'trimming' | 'loading' | 'repairing';

/** Structural member view — intentionally decoupled from the server schema. */
export interface CrewAvatarMember {
  id: string;
  role: string;
  x: number;
  z: number;
  /** Optional server task; drives the role animation when present. */
  task?: CrewAvatarTask;
}

/** Optional constructor wiring (keeps `new CrewAvatars(parent)` working). */
export interface CrewAvatarsOptions {
  /** Shared library; when omitted CrewAvatars owns (and disposes) a private one. */
  library?: ModelLibrary;
  /** 'high' uses GLB crew once loaded; 'low' always uses the procedural avatar. */
  detail?: 'high' | 'low';
}

/** Deck height in ship-local space (matches the player-avatar deck height). */
const DECK_Y = 1.9;

/** Exponential position easing rate, 1/s. */
const POSITION_LERP_RATE = 10;

/** Idle bob frequency, rad/s (procedural fallback only). */
const IDLE_BOB_RATE = 2 * Math.PI * 0.55;

/** Idle bob amplitude, metres (procedural fallback only). */
const IDLE_BOB_AMOUNT = 0.05;

/** Hammer swing frequency while repairing, rad/s (~1.7 strokes a second). */
const SWING_RATE = 2 * Math.PI * 1.7;

/** Hammer rest angle about the local X axis (tilted back, ready to strike). */
const SWING_BASE = -1.0;

/** Forward/down travel of the hammer stroke, radians. */
const SWING_AMPLITUDE = 1.35;

/** Seed for per-avatar animation phase so crew don't bob in lockstep. */
const SEED = 0x8a71;

/** Crossfade duration when switching crew GLB actions, seconds. */
const FADE_SECONDS = 0.25;

/**
 * GLB crew normalisation, measured from `public/models/quaternius-pirate/crew.glb`:
 * the Quaternius Pirate Captain's body stands ~1.75 m tall with feet at y ≈ 0
 * and faces +Z (left foot at +X). We scale to a ~1.7 m deck hand and yaw +90°
 * so he faces the bow (local +X); feet stay on the group origin, which callers
 * place on the deck line. The carried cutlass is hidden, so its y < 0 reach
 * never matters.
 */
const CREW_SOURCE_HEIGHT = 1.75;
const CREW_TARGET_HEIGHT = 1.7;
const CREW_SCALE = CREW_TARGET_HEIGHT / CREW_SOURCE_HEIGHT;
const CREW_FEET_Y = 0;
const CREW_FACE_YAW = Math.PI / 2;

/** GLB animation clip (4th pipe-segment of the exported action name) per task. */
const TASK_CLIPS: Record<CrewAvatarTask, readonly string[]> = {
  idle: ['Idle'],
  trimming: ['Wave'],
  loading: ['Sword'],
  repairing: ['Punch'],
};

interface AvatarEntry {
  group: THREE.Group;
  target: THREE.Vector3;
  task: CrewAvatarTask;
  phase: number;
  time: number;
  /** Procedural fallback: child that carries idle bob / hammer swing. */
  bob: THREE.Group | null;
  /** Procedural fallback: hammer held by repair hands, or null. */
  hammer: THREE.Group | null;
  /** GLB path: cloned scene root (child of `group`). */
  model: THREE.Group | null;
  /** GLB path: per-instance mixer. */
  mixer: THREE.AnimationMixer | null;
  /** GLB path: action name -> action. */
  actions: Map<string, THREE.AnimationAction> | null;
  /** GLB path: the currently playing action. */
  current: THREE.AnimationAction | null;
}

function roleColor(role: string): number {
  switch (role) {
    case 'gunner':
      return 0xe0563f;
    case 'sailor':
      return 0x3fb8a8;
    case 'repair':
      return 0xe8b64c;
    default:
      return 0xe8b64c;
  }
}

/** Mirror the game's mobile profile gate so the default LOD matches it. */
function isMobileProfile(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760)
  );
}

/**
 * Extract the clean action name from a Quaternius exported clip name. Their
 * GLBs name actions as `Armature|Armature|Armature|<Action>|Armature|<Action>`,
 * so the meaningful token is the 4th pipe-segment; fall back to the last.
 */
function clipActionName(raw: string): string {
  const parts = raw.split('|');
  return (parts[3] ?? '').trim() || (parts[parts.length - 1] ?? '').trim() || raw;
}

export class CrewAvatars {
  readonly group = new THREE.Group();

  private readonly library: ModelLibrary;
  private readonly ownsLibrary: boolean;
  private readonly detail: 'high' | 'low';
  private members: CrewAvatarMember[] = [];
  private readonly entries = new Map<string, AvatarEntry>();
  private modelReady = false;
  private loadStarted = false;
  private disposed = false;
  /** Settles when the crew GLB load finishes (success or fallback). */
  private readonly ready: Promise<void>;

  private readonly bodyGeometry: THREE.CapsuleGeometry;
  private readonly headGeometry: THREE.SphereGeometry;
  private readonly hammerHandleGeometry: THREE.CylinderGeometry;
  private readonly hammerHeadGeometry: THREE.BoxGeometry;
  private readonly hammerMaterial: THREE.MeshStandardMaterial;
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly roleMaterials = new Map<string, THREE.MeshStandardMaterial>();
  private readonly roleAvatarGeometries = new Map<string, THREE.BufferGeometry>();
  private readonly rng = createSeededRandom(SEED);

  constructor(parent?: THREE.Object3D, options: CrewAvatarsOptions = {}) {
    this.group.name = 'crew-avatars';
    this.library = options.library ?? new ModelLibrary();
    this.ownsLibrary = !options.library;
    this.detail = options.detail ?? (isMobileProfile() ? 'low' : 'high');

    this.bodyGeometry = new THREE.CapsuleGeometry(0.2, 0.38, 4, 10);
    this.headGeometry = new THREE.SphereGeometry(0.16, 10, 8);
    this.hammerHandleGeometry = new THREE.CylinderGeometry(0.035, 0.035, 0.62, 6);
    this.hammerHeadGeometry = new THREE.BoxGeometry(0.18, 0.12, 0.26);
    this.hammerMaterial = new THREE.MeshStandardMaterial({
      color: 0x6b4a2f,
      roughness: 0.45,
      metalness: 0.35,
    });
    this.geometries.push(
      this.bodyGeometry,
      this.headGeometry,
      this.hammerHandleGeometry,
      this.hammerHeadGeometry,
    );
    this.materials.push(this.hammerMaterial);
    if (parent) parent.add(this.group);

    // Kick the crew GLB fetch immediately; `whenReady()` exposes the same
    // promise so capture paths can await the model (and the fallback swap)
    // before freezing a frame.
    this.ready = this.detail === 'high' ? this.preloadCrew() : Promise.resolve();
  }

  /**
   * Reconcile the visible avatars with the latest roster. New members appear at
   * their target position immediately; removed members are detached.
   */
  sync(members: readonly CrewAvatarMember[]): void {
    this.members = [...members];
    const seen = new Set<string>();
    for (const member of members) {
      seen.add(member.id);
      let entry = this.entries.get(member.id);
      if (!entry) {
        entry = this.createEntry(member);
        this.entries.set(member.id, entry);
        this.group.add(entry.group);
      } else if (this.detail === 'high' && this.modelReady && !entry.model) {
        // The crew GLB landed after the procedural fallback was built; swap
        // the capsule avatar for the modelled sailor in place.
        this.releaseEntry(entry);
        this.group.remove(entry.group);
        entry = this.createEntry(member);
        this.entries.set(member.id, entry);
        this.group.add(entry.group);
      }
      entry.target.set(member.x, DECK_Y, member.z);
      entry.task = member.task ?? 'idle';
      if (entry.mixer) this.playTask(entry, entry.task);
    }
    for (const [id, entry] of this.entries) {
      if (!seen.has(id)) {
        this.releaseEntry(entry);
        this.group.remove(entry.group);
        this.entries.delete(id);
      }
    }
  }

  /** Ease every avatar toward its latest target position and play its animation. */
  update(delta: number): void {
    const dt = Math.max(0, delta);
    const t = 1 - Math.exp(-POSITION_LERP_RATE * dt);
    for (const entry of this.entries.values()) {
      entry.group.position.lerp(entry.target, t);
      entry.time += dt;
      if (entry.mixer) {
        entry.mixer.update(dt);
      } else {
        this.animateProcedural(entry);
      }
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const entry of this.entries.values()) this.releaseEntry(entry);
    this.entries.clear();
    this.members.length = 0;

    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    this.geometries.length = 0;
    this.materials.length = 0;
    this.roleMaterials.clear();
    this.roleAvatarGeometries.clear();
    this.group.clear();
    if (this.ownsLibrary) this.library.dispose();
  }

  /**
   * Resolve once the crew GLB has loaded (or its load settled as a failure).
   * Capture paths should `await crewAvatars.whenReady()` before freezing a
   * frame so modelled crew (not the procedural fallback) are visible. Safe to
   * call any number of times; it never rejects.
   */
  whenReady(): Promise<void> {
    return this.ready;
  }

  /** True once the crew GLB is available for cloning. */
  isModelReady(): boolean {
    return this.modelReady;
  }

  /** Load the crew GLB once; swap existing fallback avatars when it lands. */
  private async preloadCrew(): Promise<void> {
    if (this.loadStarted) return;
    this.loadStarted = true;
    await this.library.load(['crew']);
    if (this.disposed) return;
    this.modelReady = this.library.has('crew');
    if (this.modelReady && this.members.length > 0) this.sync(this.members);
  }

  private createEntry(member: CrewAvatarMember): AvatarEntry {
    const handle = this.detail === 'high' && this.modelReady ? this.library.create('crew') : null;
    if (handle) return this.buildGlbEntry(member, handle);
    return this.buildProceduralEntry(member);
  }

  private buildGlbEntry(member: CrewAvatarMember, handle: ModelHandle): AvatarEntry {
    const group = new THREE.Group();
    const model = handle.scene;
    model.name = `crew-${member.role}`;
    model.scale.setScalar(CREW_SCALE);
    model.rotation.y = CREW_FACE_YAW;
    model.position.y = -CREW_FEET_Y * CREW_SCALE;
    // The Pirate Captain carries a cutlass; hide it so crew read as sailors.
    // GLTFLoader leaves castShadow off, so opt the visible meshes in here.
    model.traverse((child) => {
      if (child.name.toLowerCase().includes('weapon')) {
        child.visible = false;
        return;
      }
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    group.add(model);

    const mixer = new THREE.AnimationMixer(model);
    const actions = new Map<string, THREE.AnimationAction>();
    for (const clip of handle.animations) {
      const name = clipActionName(clip.name);
      if (name) actions.set(name, mixer.clipAction(clip));
    }

    const entry: AvatarEntry = {
      group,
      target: new THREE.Vector3(member.x, DECK_Y, member.z),
      task: member.task ?? 'idle',
      phase: 0,
      time: 0,
      bob: null,
      hammer: null,
      model,
      mixer,
      actions,
      current: null,
    };
    group.position.set(member.x, DECK_Y, member.z);
    this.playTask(entry, entry.task);
    return entry;
  }

  private playTask(entry: AvatarEntry, task: CrewAvatarTask): void {
    if (!entry.mixer || !entry.actions) return;
    const candidates = TASK_CLIPS[task] ?? TASK_CLIPS.idle;
    let next: THREE.AnimationAction | null = null;
    for (const candidate of candidates) {
      next = entry.actions.get(candidate) ?? null;
      if (next) break;
    }
    if (!next) next = entry.actions.get('Idle') ?? null;
    if (!next) next = entry.actions.values().next().value ?? null;
    if (!next || next === entry.current) return;

    if (entry.current) entry.current.fadeOut(FADE_SECONDS);
    next.reset().fadeIn(FADE_SECONDS).play();
    entry.current = next;
  }

  private buildProceduralEntry(member: CrewAvatarMember): AvatarEntry {
    const group = new THREE.Group();
    const material = this.roleMaterial(member.role);

    const bob = new THREE.Group();
    bob.name = 'avatar-bob';
    group.add(bob);

    // Body + head share a role material, so they are merged into one mesh per
    // role to halve the per-avatar draw calls.
    const avatar = new THREE.Mesh(this.roleAvatarGeometry(member.role), material);
    avatar.castShadow = true;
    bob.add(avatar);

    let hammer: THREE.Group | null = null;
    if (member.role === 'repair') {
      hammer = this.buildHammer();
      bob.add(hammer);
    }

    const entry: AvatarEntry = {
      group,
      bob,
      hammer,
      target: new THREE.Vector3(member.x, DECK_Y, member.z),
      task: member.task ?? 'idle',
      phase: this.rng() * Math.PI * 2,
      time: 0,
      model: null,
      mixer: null,
      actions: null,
      current: null,
    };
    group.position.set(member.x, DECK_Y, member.z);
    return entry;
  }

  private buildHammer(): THREE.Group {
    const hammer = new THREE.Group();
    hammer.name = 'repair-hammer';

    const handle = new THREE.Mesh(this.hammerHandleGeometry, this.hammerMaterial);
    handle.position.y = 0.3;
    hammer.add(handle);

    const head = new THREE.Mesh(this.hammerHeadGeometry, this.hammerMaterial);
    head.position.set(0, 0.62, 0);
    hammer.add(head);

    // Pivot the hammer at the repair hand's shoulder. A rotation.x swing tips
    // the head forward and down in front of the body.
    hammer.position.set(0.26, 1.0, 0);
    hammer.rotation.x = SWING_BASE;
    return hammer;
  }

  private animateProcedural(entry: AvatarEntry): void {
    if (entry.task === 'repairing' && entry.hammer) {
      const stroke = Math.sin(entry.time * SWING_RATE + entry.phase) * 0.5 + 0.5;
      entry.hammer.rotation.x = SWING_BASE + stroke * SWING_AMPLITUDE;
      if (entry.bob) entry.bob.position.y = 0;
    } else {
      if (entry.hammer) entry.hammer.rotation.x = SWING_BASE;
      if (entry.bob) {
        entry.bob.position.y = Math.sin(entry.time * IDLE_BOB_RATE + entry.phase) * IDLE_BOB_AMOUNT;
      }
    }
  }

  /** Stop an entry's mixer and detach its children without disposing shared GLB resources. */
  private releaseEntry(entry: AvatarEntry): void {
    if (entry.mixer) {
      entry.mixer.stopAllAction();
      entry.current = null;
    }
    entry.group.clear();
  }

  /**
   * Build (once per role) a single geometry that merges the capsule body and
   * sphere head at their local offsets, so one mesh draws the whole avatar.
   */
  private roleAvatarGeometry(role: string): THREE.BufferGeometry {
    let geometry = this.roleAvatarGeometries.get(role);
    if (!geometry) {
      const body = this.bodyGeometry.clone();
      body.translate(0, 0.62, 0);
      const head = this.headGeometry.clone();
      head.translate(0, 1.12, 0);
      geometry = mergeGeometries([body, head], false);
      body.dispose();
      head.dispose();
      this.roleAvatarGeometries.set(role, geometry);
      this.geometries.push(geometry);
    }
    return geometry;
  }

  private roleMaterial(role: string): THREE.MeshStandardMaterial {
    let material = this.roleMaterials.get(role);
    if (!material) {
      material = new THREE.MeshStandardMaterial({
        color: roleColor(role),
        roughness: 0.55,
        metalness: 0.05,
      });
      this.roleMaterials.set(role, material);
      this.materials.push(material);
    }
    return material;
  }
}
